import api from '@shared/services/api';
import {
  AdminInvitationsPage,
  AdminInvitationsPageResponse,
  CreateInvitationRequest,
  DeleteInvitationResult,
  Invitation,
  InvitationApprovalStatus,
  InvitationForReview,
  InvitationResponse,
  InvitationsForReviewResponse,
  InvitationsResponse,
  InvitationStatus,
  InvitationType,
  ReviewInvitationRequest,
} from '../types/invitation';

export class InvitationService {

  static BASE_URL = '/invitations';
  static ORGANIZATION_BASE_URL = '/organizations';
  static ADMIN_BASE_URL = '/admin';

  /**
   * Create a new invitation
   * @param organizationId Organization ID
   * @param data Invitation data
   */
  static async createInvitation(
    organizationId: string,
    data: CreateInvitationRequest
  ): Promise<Invitation> {
    const requestData = {
      ...data,
      type: InvitationType.ORGANIZATION
    };
    const response = await api.post<InvitationResponse>(
      `${this.ORGANIZATION_BASE_URL}/${organizationId}/invitations`,
      requestData
    );
    return response.data.data;
  }

  /**
   * Create a new admin invitation
   * @param data Invitation data
   */
  static async createAdminInvitation(
    data: CreateInvitationRequest
  ): Promise<Invitation> {
    const requestData = {
      ...data,
      type: InvitationType.ADMIN
    };
    const response = await api.post<InvitationResponse>(
      `${this.ADMIN_BASE_URL}/invitations`,
      requestData
    );
    return response.data.data;
  }

  /**
   * Get all invitations for an organization
   * @param organizationId Organization ID
   */
  static async getOrganizationInvitations(organizationId: string): Promise<Invitation[]> {
    const response = await api.get<InvitationsResponse>(
      `${this.ORGANIZATION_BASE_URL}/${organizationId}/invitations`
    );
    return response.data.data;
  }

  /**
   * One page of admin invitations. Filtering, searching and paging all happen
   * in Firestore, so the browser only ever holds the rows it shows.
   * @param params Status filter, email prefix to search, page and page size
   */
  static async getAdminInvitations(params: {
    status?: InvitationStatus;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<AdminInvitationsPage> {
    const response = await api.get<AdminInvitationsPageResponse>(
      `${this.ADMIN_BASE_URL}/invitations`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Delete an invitation
   * @param invitationId Invitation ID
   * @param isAdminInvitation Whether this is an admin invitation
   */
  static async deleteInvitation(
    invitationId: string,
    isAdminInvitation: boolean = false
  ): Promise<DeleteInvitationResult> {
    const baseUrl = isAdminInvitation ? this.ADMIN_BASE_URL : this.ORGANIZATION_BASE_URL;
    const response = await api.delete<{ data: DeleteInvitationResult }>(
      `${baseUrl}${this.BASE_URL}/${invitationId}`
    );
    return response.data.data;
  }

  /**
   * Resend an invitation
   * @param invitationId Invitation ID
   * @param isAdminInvitation Whether this is an admin invitation
   */
  static async resendInvitation(invitationId: string, isAdminInvitation: boolean = false): Promise<Invitation> {
    const baseUrl = isAdminInvitation ? this.ADMIN_BASE_URL : this.ORGANIZATION_BASE_URL;
    const response = await api.post<InvitationResponse>(
      `${baseUrl}${this.BASE_URL}/${invitationId}/resend`
    );
    return response.data.data;
  }

  /**
   * Member invitations for the GCCA review screen.
   * @param approvalStatus Optional filter; omit to get every status
   */
  static async getInvitationsForReview(params: {
    approvalStatus?: InvitationApprovalStatus;
    page?: number;
    limit?: number;
  } = {}): Promise<{
    invitations: InvitationForReview[];
    total: number;
    currentPage: number;
    hasNextPage: boolean;
  }> {
    const response = await api.get<InvitationsForReviewResponse>(
      `${this.ADMIN_BASE_URL}/member-invitations`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Approve or reject a member invitation.
   * @param invitationId Invitation ID
   * @param data Decision and optional internal comment
   */
  static async reviewInvitation(
    invitationId: string,
    data: ReviewInvitationRequest
  ): Promise<Invitation> {
    const response = await api.put<InvitationResponse>(
      `${this.ADMIN_BASE_URL}/member-invitations/${invitationId}/review`,
      data
    );
    return response.data.data;
  }

  /**
   * Accept an invitation
   * @param invitationId Invitation ID
   * @param isAdminInvitation Whether this is an admin invitation
   */
  static async acceptInvitation(invitationId: string, isAdminInvitation: boolean = false): Promise<Invitation> {
    const baseUrl = isAdminInvitation ? this.ADMIN_BASE_URL : this.ORGANIZATION_BASE_URL;
    const response = await api.post<InvitationResponse>(
      `${baseUrl}${this.BASE_URL}/${invitationId}/accept`
    );
    return response.data.data;
  }
}
